import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'constants/locale';
import ZoocasaTheme from '@tenants/definitions/zoocasa/theme-config';
import { type HomeConfig, type TenantConfigHome, ZOOCASA_TENANT_ID, ZOOCASA_THEME_NAME } from '@tenants/types';

import type { ContentHosts, FooterConfig, TenantConfigFooter, ThemeConfig, ZOOCASA_TENANT_ID_TYPE } from '@tenants/types';
import type { Tenant } from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { Namespaces } from 'types/locales';
import { createHomeConfig } from '@tenants/definitions/zoocasa/home-config';

//#region Public Constants

export const ZOOCASA_L18N_NAMESPACES = [
  Namespaces.COMMON,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
] as const;

//#endregion

//#region Public Types
export type ZoocasaTenantConfig = TenantConfigHome & TenantConfigFooter;
export type ZoocasaTenant = Tenant<ZOOCASA_TENANT_ID_TYPE, ThemeConfig<typeof ZOOCASA_THEME_NAME>, ZoocasaTenantConfig>;
//#endregion

//#region Config Factories
// Use default CA config for build time, will be overridden dynamically on client side
const home: HomeConfig = createHomeConfig(CountryCodeList.CANADA);
const footer: FooterConfig = createFooterConfig();
//#endregion

//#region Tenant Definition
export const zoocasaTenant: ZoocasaTenant = {
  id: ZOOCASA_TENANT_ID,
  hosts: configJSON.hosts[ZOOCASA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[ZOOCASA_TENANT_ID] as ContentHosts,
  blogApiUrl: configJSON.blogApi[ZOOCASA_TENANT_ID],
  theme: ZoocasaTheme,
  localization: {
    languages: [SiteLanguage.English, SiteLanguage.French],
    countries: [CountryCodeList.CANADA, CountryCodeList.UNITED_STATES],
    namespaces: ZOOCASA_L18N_NAMESPACES,
    defaultLanguage: SiteLanguage.English,
    defaultCountry: CountryCodeList.CANADA,
  },
  config: {
    home,
    footer,
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${ZOOCASA_TENANT_ID}"`,
    forceTenantFeatureValue: ZOOCASA_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: false,
    AccessibilityWidget: true,
    TrustArc: {
      enabled: true,
      domain: 'zoocasa.com',
    },
  },
  googleTagManager: {
    containerIds: {
      defaultContainerId: configJSON.googleTagManager.containerId,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '',
  },
  sitemap: {
    url: [
      'https://www.zoocasa.com/sitemaps/area-pages/sitemap.xml',
      'https://www.zoocasa.com/sitemaps/address-pages/sitemap.xml',
      'https://www.zoocasa.com/sitemaps/root-pages/sitemap-index.xml',
      'https://www.zoocasa.com/sitemaps/cms/sitemap-index.xml',
      'https://www.zoocasa.com/blog/sitemap_index.xml',
    ],
  },
  pvr: {
    phoneNumber: '1-844-683-4663',
  },
};//#endregion
